import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { toast } from 'sonner'
import { useCurator } from '../../contexts/CuratorContext'
import { SheetGrip } from '../SheetGrip'
import { X, MessageCircle, CornerDownRight } from 'lucide-react'
import { resolveMediaUrl } from '../../services/api'

const F = 'Roboto, sans-serif'

/**
 * #266/#270/#286: единый инбокс комментариев куратора. Вкладка «Всё» убрана —
 * только Питание/Тренировки. Сообщения сгруппированы в треды по контексту
 * (тег дня питания / тренировки), ответ пишется внутри треда — всегда видно,
 * на что именно идёт ответ. «Ответить» под конкретным сообщением добавляет
 * цитату (короткий фрагмент), которая уходит вместе с текстом.
 */
export interface InboxComment {
  id: string
  text: string
  createdAt: string
  authorType: 'CURATOR' | 'CLIENT' | string
  authorName: string | null
  avatarUrl: string | null
  category: 'NUTRITION' | 'WORKOUT' | 'OTHER' | string
  emoji: string
  contextLabel: string
  date: string | null
  reply: { targetType: string; targetId: string } | null
  isUnread: boolean
  // #337: ключи группировки (приходят с бэкенда для тренировочных комментариев).
  targetType?: string
  exerciseKey?: string | null
  exerciseName?: string | null
  workoutEntryId?: string | null
  workoutDayNumber?: number | null
}

/**
 * #337: три вкладки вместо двух.
 *  - NUTRITION — как раньше, треды по дням питания.
 *  - EXERCISE  — ветка = упражнение, внутри вся история комментариев по нему за
 *    все даты (клиент просил видеть историю по конкретному упражнению в одном
 *    месте, а не сплошным списком «упражнение × день»).
 *  - WORKOUT   — ветка = тренировка (день), внутри всё общение по ней: и по
 *    самой тренировке, и по её упражнениям.
 */
export type InboxTab = 'NUTRITION' | 'EXERCISE' | 'WORKOUT'

interface Thread {
  key: string
  emoji: string
  /** Заголовок ветки: день питания / упражнение / тренировка. */
  title: string
  /** Подпись справа от заголовка (дата тренировки или «N обсуждений»). */
  subtitle: string | null
  contextLabel: string
  date: string | null
  category: string
  mode: InboxTab
  reply: { targetType: string; targetId: string } | null
  messages: InboxComment[]
  lastAt: string
  unread: number
}

interface Props {
  open: boolean
  onClose: () => void
  comments: InboxComment[]
  initialTab?: InboxTab
  /**
   * #470: открыть шторку сразу на конкретной ветке (ключ треда, напр.
   * `ex:<exerciseId>`). Ветка уезжает наверх списка, а вкладка выбирается по
   * префиксу ключа. Нужно для перехода «из карточки последнего комментария на
   * экране упражнения — во всю переписку по нему»: раньше туда вёл только общий
   * значок 💬, и найти в нём нужное упражнение было нечем.
   */
  focusThreadKey?: string | null
  /** Отправить ответ в контекст треда (таргет дня питания/тренировки). */
  onReply: (target: { targetType: string; targetId: string }, text: string) => Promise<void>
  /** Подпись автора «своих» сообщений (curator-web передаёт имя клиента). */
  perspective?: 'CLIENT' | 'CURATOR'
}

const TABS: { key: InboxTab; label: string }[] = [
  { key: 'NUTRITION', label: 'Питание' },
  { key: 'EXERCISE', label: 'Упражнения' },
  { key: 'WORKOUT', label: 'Тренировки' },
]

/** Какие комментарии попадают во вкладку. */
export function matchesTab(c: InboxComment, tab: InboxTab): boolean {
  if (tab === 'NUTRITION') return c.category === 'NUTRITION'
  if (tab === 'EXERCISE') return c.category === 'WORKOUT' && !!c.exerciseKey
  // WORKOUT: и комментарии к самой тренировке, и к её упражнениям.
  return c.category === 'WORKOUT' && !!c.workoutEntryId
}

function fmtDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}
function fmtTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}
function excerpt(text: string, max = 60): string {
  const t = text.replace(/\s+/g, ' ').trim()
  return t.length > max ? `${t.slice(0, max - 1)}…` : t
}

/**
 * #470: ключ ветки упражнения в инбоксе.
 *
 * Формула зеркалит `exerciseKey` из comments-inbox.service на бэке: у обычных
 * упражнений это `exerciseId`, у «своих» (без id) — `custom:<имя>`. Держим её
 * одной функцией, потому что теперь ключ строит не только сам инбокс, но и
 * экран упражнения (переход «вся переписка по упражнению»). Разъедутся —
 * переход молча откроет инбокс без нужной ветки, и это никак не заметно.
 */
export function exerciseThreadKey(
  exerciseId?: string | null,
  customName?: string | null,
): string | null {
  if (exerciseId) return `ex:${exerciseId}`
  if (customName) return `ex:custom:${customName}`
  return null
}

/** #470: на какой вкладке живёт ветка с таким ключом. */
export function tabForThreadKey(key: string | null | undefined, fallback: InboxTab): InboxTab {
  if (key?.startsWith('ex:')) return 'EXERCISE'
  if (key?.startsWith('wo:')) return 'WORKOUT'
  return fallback
}

/** #470: ветку, ради которой открыли шторку, поднимаем в начало списка. */
export function focusFirst<T extends { key: string }>(threads: T[], focusKey: string | null | undefined): T[] {
  if (!focusKey) return threads
  const i = threads.findIndex((t) => t.key === focusKey)
  return i <= 0 ? threads : [threads[i], ...threads.slice(0, i), ...threads.slice(i + 1)]
}

export function buildThreads(comments: InboxComment[], mode: InboxTab = 'NUTRITION'): Thread[] {
  const map = new Map<string, Thread>()
  for (const c of comments) {
    // #337: ключ ветки зависит от вкладки. EXERCISE склеивает разные даты одного
    // упражнения, WORKOUT — все упражнения одной тренировки.
    let key: string
    let title: string
    let subtitle: string | null
    if (mode === 'EXERCISE') {
      key = `ex:${c.exerciseKey}`
      title = c.exerciseName || c.contextLabel
      subtitle = null
    } else if (mode === 'WORKOUT') {
      key = `wo:${c.workoutEntryId}`
      title = c.workoutDayNumber != null ? `Тренировка · День ${c.workoutDayNumber}` : 'Тренировка'
      subtitle = fmtDate(c.date)
    } else {
      key = c.reply ? `${c.reply.targetType}:${c.reply.targetId}` : `${c.category}:${c.contextLabel}:${c.date ?? ''}`
      title = c.contextLabel
      subtitle = fmtDate(c.date)
    }
    let t = map.get(key)
    if (!t) {
      t = { key, emoji: c.emoji, title, subtitle, contextLabel: c.contextLabel, date: c.date, category: c.category, mode, reply: c.reply, messages: [], lastAt: c.createdAt, unread: 0 }
      map.set(key, t)
    }
    t.messages.push(c)
    if (c.createdAt > t.lastAt) t.lastAt = c.createdAt
    if (c.isUnread) t.unread++
    // У части сообщений треда reply может отсутствовать (например, коммент к
    // приёму пищи мапится в таргет дня) — берём первый непустой.
    if (!t.reply && c.reply) t.reply = c.reply
  }
  const threads = [...map.values()]
  for (const t of threads) {
    t.messages.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    if (t.mode === 'EXERCISE') {
      // Ответ должен уйти в САМУЮ СВЕЖУЮ запись упражнения: у каждой даты свой
      // targetId, иначе комментарий приклеится к тренировке недельной давности.
      const last = [...t.messages].reverse().find((m) => m.reply)
      if (last?.reply) t.reply = last.reply
      const dates = new Set(t.messages.map((m) => (m.date ?? '').slice(0, 10)).filter(Boolean))
      t.subtitle = dates.size > 1 ? `${dates.size} тренировок` : fmtDate(t.messages[0]?.date ?? null)
    } else if (t.mode === 'WORKOUT') {
      // Вся ветка — про одну тренировку: отвечаем в неё целиком.
      const wid = t.messages.find((m) => m.workoutEntryId)?.workoutEntryId
      if (wid) t.reply = { targetType: 'WORKOUT_ENTRY', targetId: wid }
    }
  }
  threads.sort((a, b) => b.lastAt.localeCompare(a.lastAt))
  return threads
}

export function CommentsInboxSheet({ open, onClose, comments, initialTab = 'NUTRITION', focusThreadKey = null, onReply, perspective = 'CLIENT' }: Props) {
  const [tab, setTab] = useState<InboxTab>(initialTab)
  // Композер: тред + опциональная цитата конкретного сообщения.
  const [composer, setComposer] = useState<{ threadKey: string; quote: InboxComment | null } | null>(null)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  // Админ «за пользователя» (только чтение) — и на клиентской, и на кураторской
  // стороне: ответ ушёл бы с его токеном и молча получил 403. Композер ему не
  // открываем, а объясняем тостом, как guardReadOnly на экране программы.
  const { isAdminView } = useCurator()
  const openComposer = (threadKey: string, quote: InboxComment | null) => {
    if (isAdminView) {
      toast('Режим просмотра', { description: 'Вы смотрите аккаунт клиента — изменения недоступны.' })
      return
    }
    setComposer({ threadKey, quote })
    setDraft('')
  }

  const unread = useMemo(() => {
    const u = { NUTRITION: 0, EXERCISE: 0, WORKOUT: 0 } as Record<InboxTab, number>
    for (const c of comments) {
      if (!c.isUnread) continue
      // Комментарий к упражнению виден и во вкладке «Упражнения», и в ветке
      // своей тренировки — считаем его в обеих (это одно и то же сообщение).
      for (const tab of TABS) if (matchesTab(c, tab.key)) u[tab.key]++
    }
    return u
  }, [comments])

  useEffect(() => {
    if (!open) return
    // #470: пришли по ссылке на конкретную ветку — вкладку выбирает ключ, а не
    // непрочитанные. Иначе переход «показать всю переписку по упражнению»
    // уводил бы на соседнюю вкладку, где этой ветки нет.
    if (focusThreadKey) {
      setTab(tabForThreadKey(focusThreadKey, initialTab))
      setComposer(null)
      setDraft('')
      return
    }
    // Если во вкладке по умолчанию пусто по непрочитанным, а в соседней есть —
    // открываем сразу её.
    let t = initialTab
    if (unread[initialTab] === 0) {
      const withUnread = TABS.find((x) => unread[x.key] > 0)
      if (withUnread) t = withUnread.key
    }
    setTab(t)
    setComposer(null)
    setDraft('')
    // Снимок непрочитанных нужен только на момент открытия.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTab, focusThreadKey])

  // #265: пока окно открыто — гасим вертикальный свайп Telegram (иначе скролл ленты
  // сворачивает мини-апп); drag-жеста нет, закрытие только ✕/стрелкой-вниз.
  useEffect(() => {
    if (!open) return
    const wa = (window as unknown as { Telegram?: { WebApp?: any } }).Telegram?.WebApp
    if (!wa) return
    const wasEnabled = wa.isVerticalSwipesEnabled !== false
    try { wa.expand?.(); wa.disableVerticalSwipes?.() } catch { /* старые клиенты */ }
    return () => { if (wasEnabled) { try { wa.enableVerticalSwipes?.() } catch { /* игнор */ } } }
  }, [open])

  const threads = useMemo(() => {
    const built = buildThreads(comments.filter((c) => matchesTab(c, tab)), tab)
    // #470: ветка, ради которой шторку и открыли, стоит первой — иначе она
    // теряется среди прочих упражнений, и переход «читать историю» упирается в
    // тот же поиск глазами, от которого он и должен избавлять.
    return focusFirst(built, focusThreadKey)
  }, [comments, tab, focusThreadKey])

  const submitReply = async (thread: Thread) => {
    const text = draft.trim()
    if (!text || sending || !thread.reply) return
    setSending(true)
    try {
      // Цитата уходит в тексте — обе стороны видят, на какое сообщение ответ.
      const payload = composer?.quote ? `↩️ «${excerpt(composer.quote.text)}»\n${text}` : text
      await onReply(thread.reply, payload)
      setDraft('')
      setComposer(null)
    } catch {
      // Черновик остаётся в поле — можно отправить ещё раз.
      toast.error('Не удалось отправить')
    } finally {
      setSending(false)
    }
  }

  const otherLabel = perspective === 'CLIENT' ? 'Куратор' : 'Клиент'

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 410,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="comments-inbox-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              height: '85vh', display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4 }}>
              <SheetGrip onClose={onClose} />
            </div>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '6px 18px 8px', gap: 12, flexShrink: 0 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12, background: 'rgba(108,92,231,0.14)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <MessageCircle size={20} color="#6C5CE7" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1C2D41' }}>
                  {perspective === 'CLIENT' ? 'Комментарии куратора' : 'Чат с клиентом'}
                </div>
                <div style={{ fontSize: 12, color: '#9AA5B4', marginTop: 1 }}>Обсуждения по дням питания и тренировок</div>
              </div>
              <button
                data-testid="comments-inbox-close"
                onClick={onClose}
                style={{
                  width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, padding: '2px 16px 10px', flexShrink: 0 }}>
              {TABS.map((t) => {
                const active = tab === t.key
                const cnt = unread[t.key]
                return (
                  <button
                    key={t.key}
                    data-testid={`comments-tab-${t.key}`}
                    onClick={() => { setTab(t.key); setComposer(null) }}
                    style={{
                      flex: 1, height: 34, borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: F,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      background: active ? '#6C5CE7' : 'white',
                      color: active ? 'white' : '#3D4A5C',
                      fontSize: 13, fontWeight: 700,
                      boxShadow: active ? 'none' : '0 1px 2px rgba(26,58,92,0.06)',
                    }}
                  >
                    {t.label}
                    {cnt > 0 && (
                      <span style={{
                        minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
                        background: active ? 'rgba(255,255,255,0.28)' : '#FF3B30', color: 'white',
                        fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}>{cnt}</span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Threads */}
            <div style={{ padding: '0 14px 8px', overflowY: 'auto', flex: 1, minHeight: 80 }}>
              {threads.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center', color: '#9AA5B4', gap: 8 }}>
                  <MessageCircle size={32} color="#C4CDD6" />
                  <p style={{ fontSize: 13, margin: 0, color: '#6B7B8D', fontWeight: 600 }}>Пока нет комментариев</p>
                  <p style={{ fontSize: 11, margin: 0, lineHeight: 1.45 }}>
                    {tab === 'NUTRITION'
                      ? 'Обсуждения дней питания появятся здесь.'
                      : tab === 'EXERCISE'
                        ? 'Здесь соберётся история комментариев по каждому упражнению.'
                        : 'Обсуждения тренировок появятся здесь.'}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {threads.map((t) => {
                    const chipColor = t.category === 'WORKOUT' ? '#4A6CF7' : '#6C5CE7'
                    const composing = composer?.threadKey === t.key
                    return (
                      <div key={t.key} data-testid="comments-thread" style={{
                        background: 'white', border: `1px solid ${t.unread > 0 ? 'rgba(255,59,48,0.35)' : 'rgba(26,58,92,0.08)'}`,
                        borderRadius: 14, padding: '11px 13px', display: 'flex', flexDirection: 'column', gap: 8,
                        boxShadow: '0 1px 3px rgba(26,58,92,0.04)',
                      }}>
                        {/* Заголовок треда — тег дня питания/тренировки */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span data-testid="comments-thread-tag" style={{ fontSize: 12, fontWeight: 800, color: chipColor, background: `${chipColor}14`, borderRadius: 7, padding: '3px 9px' }}>
                            {t.emoji} {t.title}{t.subtitle ? ` · ${t.subtitle}` : ''}
                          </span>
                          {t.unread > 0 && (
                            <span style={{ fontSize: 9, fontWeight: 800, color: 'white', background: '#FF3B30', borderRadius: 6, padding: '2px 6px', textTransform: 'uppercase' }}>
                              {t.unread > 1 ? `${t.unread} новых` : 'Новый'}
                            </span>
                          )}
                        </div>

                        {t.messages.map((c) => {
                          const isMine = perspective === 'CLIENT' ? c.authorType === 'CLIENT' : c.authorType === 'CURATOR'
                          const name = isMine ? 'Вы' : (c.authorName ?? otherLabel)
                          const grad = c.authorType === 'CLIENT' ? 'linear-gradient(135deg, #1B9E44, #34C759)' : 'linear-gradient(135deg, #6C5CE7, #A29BFE)'
                          return (
                            <div key={c.id} style={{ display: 'flex', gap: 9 }}>
                              <div style={{
                                width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', background: grad, flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12, fontWeight: 700,
                              }}>
                                {c.avatarUrl
                                  ? <img src={resolveMediaUrl(c.avatarUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  : (name[0] ?? 'К').toUpperCase()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {/* #337: внутри «сборной» ветки нужно понимать, к чему именно
                                    относится сообщение: в «Упражнениях» — дата тренировки,
                                    в «Тренировках» — название упражнения. */}
                                {t.mode === 'EXERCISE' && c.date && (
                                  <div data-testid="comment-context-note" style={{ fontSize: 10.5, fontWeight: 700, color: '#8E9BAA', marginBottom: 2 }}>
                                    {fmtDate(c.date)}
                                  </div>
                                )}
                                {t.mode === 'WORKOUT' && c.targetType === 'WORKOUT_EXERCISE' && (
                                  <div data-testid="comment-context-note" style={{ fontSize: 10.5, fontWeight: 700, color: '#8E9BAA', marginBottom: 2 }}>
                                    {c.contextLabel}
                                  </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: 12.5, fontWeight: 700, color: '#1C2D41' }}>{name}</span>
                                  {c.isUnread && (
                                    <span style={{ fontSize: 9, fontWeight: 800, color: '#FF3B30' }}>●</span>
                                  )}
                                  <span style={{ fontSize: 10, color: '#B0BAC6', marginLeft: 'auto' }}>{fmtTime(c.createdAt)}</span>
                                </div>
                                <p style={{ fontSize: 13, color: '#3D4A5C', margin: 0, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.text}</p>
                                {!isMine && t.reply && (
                                  <button
                                    data-testid="comment-reply-open"
                                    onClick={() => openComposer(t.key, c)}
                                    style={{ marginTop: 4, background: 'transparent', border: 'none', padding: 0, color: '#6C5CE7', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                  >
                                    <CornerDownRight size={12} /> Ответить
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        })}

                        {/* Композер треда */}
                        {t.reply && (composing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid rgba(26,58,92,0.07)', paddingTop: 8 }}>
                            {composer?.quote && (
                              <div data-testid="comment-reply-quote" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6B7B8D', background: 'rgba(108,92,231,0.07)', borderLeft: '3px solid #6C5CE7', borderRadius: 6, padding: '5px 8px' }}>
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  В ответ на: «{excerpt(composer.quote.text)}»
                                </span>
                                <button onClick={() => setComposer({ threadKey: t.key, quote: null })} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}>
                                  <X size={12} color="#9AA5B4" />
                                </button>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                              <textarea
                                data-testid="comment-reply-input"
                                autoFocus
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder={`Сообщение · ${t.title}${t.date ? ` · ${fmtDate(t.date)}` : ''}`}
                                rows={1}
                                style={{ flex: 1, minHeight: 38, maxHeight: 120, resize: 'vertical', fontSize: 14, padding: '9px 12px', borderRadius: 12, border: '1px solid rgba(108,92,231,0.28)', fontFamily: F, background: 'white', boxSizing: 'border-box' }}
                              />
                              <button
                                data-testid="comment-reply-send"
                                disabled={!draft.trim() || sending}
                                onClick={() => submitReply(t)}
                                style={{ flexShrink: 0, height: 38, padding: '0 14px', borderRadius: 12, border: 'none', background: !draft.trim() || sending ? 'rgba(108,92,231,0.35)' : '#6C5CE7', color: 'white', fontSize: 13, fontWeight: 700, cursor: !draft.trim() || sending ? 'default' : 'pointer', fontFamily: F }}
                              >
                                {sending ? '…' : 'Отпр.'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            data-testid="comments-thread-compose"
                            onClick={() => openComposer(t.key, null)}
                            style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', padding: 0, color: '#9AA5B4', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F }}
                          >
                            Написать сообщение…
                          </button>
                        ))}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
